import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
    this.#observeProductPrice();
    this.#observeSellingPlan();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
    this.#priceObserver?.disconnect();
    if (this.#sellingPlanPollInterval) clearInterval(this.#sellingPlanPollInterval);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /** @type {MutationObserver | undefined} */
  #priceObserver;

  /** @type {number | undefined} */
  #sellingPlanPollInterval;

  /** @type {string | null} */
  #lastSellingPlanId = null;

  /** @type {string | null} */
  #lastVariantId = null;

  /** @type {string} */
  #lastBundlePriceKey = '';

  /**
   * Observes the product-price element for changes and syncs the price
   * displayed inside the buy button (for variant changes without selling plan).
   */
  #observeProductPrice() {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    const productPriceEl = section.querySelector('product-price');
    if (!productPriceEl) return;

    this.#priceObserver = new MutationObserver(() => {
      // Only sync from product-price if no selling plan is active
      if (this.#getSellingPlanId()) return;
      // Skip if Apstle widgets are managing the price
      if (section.querySelector('.appstle_sub_widget, .ab-volume-discount-container')) return;
      this.#syncPriceFromElement(productPriceEl);
    });

    this.#priceObserver.observe(productPriceEl, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /**
   * Polls for selling_plan changes from Apstle.
   */
  #observeSellingPlan() {
    this.#pollSellingPlan();
    this.#sellingPlanPollInterval = setInterval(() => this.#pollSellingPlan(), 300);
  }

  /**
   * Checks the current selling_plan, variant, and bundle/subscription state
   * and updates the button price if changed.
   */
  #pollSellingPlan() {
    const sellingPlanId = this.#getSellingPlanId();
    const currentVariantId = this.#getCurrentVariantId();
    const bundlePriceKey = this.#getBundlePriceKey();

    const changed = sellingPlanId !== this.#lastSellingPlanId
      || currentVariantId !== this.#lastVariantId
      || bundlePriceKey !== this.#lastBundlePriceKey;
    if (!changed) return;

    this.#lastSellingPlanId = sellingPlanId;
    this.#lastVariantId = currentVariantId;
    this.#lastBundlePriceKey = bundlePriceKey;

    const priceContainer = this.refs.addToCartButton?.querySelector('.add-to-cart-price');
    if (!priceContainer) return;

    const section = this.closest('.shopify-section, dialog');

    // Read prices from both Apstle widgets
    const appstleActiveOption = section?.querySelector('.appstle_subscription_wrapper_option.appstle-active-option');
    const appstlePrice = appstleActiveOption?.querySelector('.appstle_subscription_amount')?.textContent?.trim();
    const appstleCompare = appstleActiveOption?.querySelector(
      '.appstle_subscription_amount_compare_at_price, .appstle_subscription_compare_amount'
    )?.textContent?.trim();

    const selectedTier = section?.querySelector('.ab-volume-discount-item-selected-v2');
    const tierDiscountPrice = selectedTier?.querySelector('.volume-discount-price-v2')?.textContent?.trim();
    const tierOriginalPrice = selectedTier?.querySelector('.volume-original-price-v2')?.textContent?.trim();

    // Correct volume discount total to match checkout's per-unit rounding.
    // Apstle widget uses total×(1-pct) but checkout rounds per unit first.
    let correctedTierPrice = tierDiscountPrice;
    if (selectedTier && tierDiscountPrice) {
      const badge = selectedTier.querySelector('.ab-volume-discount-item-save-badge-label-v2');
      const pctMatch = badge?.textContent?.match(/(\d+(?:\.\d+)?)\s*%/);
      const tierTitle = selectedTier.querySelector('.ab-volume-discount-item-title-label-v2');
      const qty = parseInt(tierTitle?.textContent?.trim() || '0', 10);
      if (pctMatch && qty > 0) {
        const pct = parseFloat(pctMatch[1] || '0');
        let unitCents = parseInt(this.dataset.variantPrice || '0', 10);
        if (sellingPlanId) {
          try {
            const allocScript = this.querySelector('script[data-selling-plan-allocations]');
            const allocData = allocScript ? JSON.parse(allocScript.textContent || '{}') : {};
            const vData = currentVariantId ? allocData[currentVariantId] : null;
            const alloc = vData?.allocations?.find(/** @param {any} a */ (a) => a.selling_plan_id.toString() === sellingPlanId);
            if (alloc) unitCents = alloc.price;
          } catch (e) { /* ignore */ }
        }
        if (unitCents > 0 && pct > 0) {
          const discountedUnit = Math.round(unitCents * (1 - pct / 100));
          const correctedTotal = discountedUnit * qty;
          correctedTierPrice = this.#formatMoney(correctedTotal);
        }
      }
    }

    // Correct Apstle widget prices in-place to match checkout's per-unit rounding.
    // Apstle calculates total×(1-pct) but Shopify rounds per-unit first.
    if (section) {
      const selectedQty = selectedTier
        ? parseInt(selectedTier.querySelector('.ab-volume-discount-item-title-label-v2')?.textContent?.trim() || '0', 10)
        : 1;

      // Correct subscription widget amounts (One Time + Subscribe)
      if (selectedQty > 1) {
        for (const amountEl of section.querySelectorAll('.appstle_subscription_wrapper_option .appstle_subscription_amount')) {
          if (amountEl.closest('.sr-only') || amountEl.classList.contains('sr-only')) continue;
          const shownCents = this.#parsePriceToCents(amountEl.textContent?.trim() || '');
          if (shownCents > 0) {
            const perUnit = Math.round(shownCents / selectedQty);
            const corrected = perUnit * selectedQty;
            if (corrected !== shownCents) {
              amountEl.textContent = this.#formatMoney(corrected);
            }
          }
        }
      }

      // Inject volume tier original price into subscription widget's empty compare element
      // or clear it when tier has no discount
      for (const opt of section.querySelectorAll('.appstle_subscription_wrapper_option')) {
        const compareEl = opt.querySelector('.appstle_subscription_compare_amount');
        if (!compareEl) continue;
        const nativeCompare = opt.querySelector('.appstle_subscription_amount_compare_at_price')?.textContent?.trim();
        if (nativeCompare) continue; // Apstle already provides a compare price, don't touch it
        if (tierOriginalPrice && tierOriginalPrice !== tierDiscountPrice) {
          compareEl.textContent = tierOriginalPrice;
        } else {
          compareEl.textContent = '';
        }
      }
    }

    // Re-read after potential injection
    const appstleCompareFresh = appstleActiveOption?.querySelector(
      '.appstle_subscription_amount_compare_at_price, .appstle_subscription_compare_amount'
    )?.textContent?.trim();

    // Use corrected volume price when subscription widget echoes the tier discount
    let currentPrice;
    if (correctedTierPrice && correctedTierPrice !== tierDiscountPrice) {
      currentPrice = (!appstlePrice || appstlePrice === tierDiscountPrice)
        ? correctedTierPrice
        : appstlePrice;
    } else {
      currentPrice = appstlePrice || tierDiscountPrice;
    }
    // For compare-at: prefer subscription widget, then volume widget original
    const comparePrice = (appstleCompareFresh && appstleCompareFresh !== currentPrice)
      ? appstleCompareFresh
      : (tierOriginalPrice && tierOriginalPrice !== currentPrice)
        ? tierOriginalPrice
        : null;

    if (currentPrice) {
      let html = '';
      if (comparePrice) {
        html += `<span class="compare-at-price">${comparePrice}</span> `;
      }
      html += `<span class="price">${currentPrice}</span>`;
      priceContainer.innerHTML = html;
      return;
    }

    // Fall back to allocation JSON data
    const allocationsScript = this.querySelector('script[data-selling-plan-allocations]');
    if (!allocationsScript) return;

    try {
      const data = JSON.parse(allocationsScript.textContent || '{}');
      const variantData = currentVariantId ? data[currentVariantId] : null;

      if (!sellingPlanId) {
        if (variantData) {
          this.#updatePriceDisplay(priceContainer, variantData.price, variantData.compare_at_price);
        } else {
          const variantPrice = parseInt(this.dataset.variantPrice || '0', 10);
          const variantCompare = parseInt(this.dataset.variantCompareAtPrice || '0', 10);
          this.#updatePriceDisplay(priceContainer, variantPrice, variantCompare);
        }
        return;
      }

      if (variantData && variantData.allocations) {
        const allocation = variantData.allocations.find(
          /** @param {any} a */
          (a) => a.selling_plan_id.toString() === sellingPlanId
        );
        if (allocation) {
          this.#updatePriceDisplay(priceContainer, allocation.price, allocation.compare_at_price);
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  /**
   * Returns a key representing the currently selected Apstle bundle/subscription state.
   * Tracks both volume discount widget and subscription widget so changes in either trigger a price update.
   * @returns {string}
   */
  #getBundlePriceKey() {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return '';

    const selected = section.querySelector('.ab-volume-discount-item-selected-v2');
    const tierPrice = selected?.querySelector('.volume-discount-price-v2')?.textContent?.trim() || '';
    const tierCompare = selected?.querySelector('.volume-original-price-v2')?.textContent?.trim() || '';

    const activeOption = section.querySelector('.appstle_subscription_wrapper_option.appstle-active-option');
    const subPrice = activeOption?.querySelector('.appstle_subscription_amount')?.textContent?.trim() || '';
    const subCompare = activeOption?.querySelector(
      '.appstle_subscription_amount_compare_at_price, .appstle_subscription_compare_amount'
    )?.textContent?.trim() || '';

    return `${tierPrice}|${tierCompare}|${subPrice}|${subCompare}`;
  }

  /**
   * Gets the current variant ID from the form input or URL.
   * @returns {string}
   */
  #getCurrentVariantId() {
    const form = this.closest('form');
    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input instanceof HTMLInputElement && input.value) return input.value;
    }
    const url = new URL(window.location.href);
    return url.searchParams.get('variant') || '';
  }

  /**
   * Reads the selling_plan value from multiple sources:
   * 1. Form inputs (hidden, radio, select)
   * 2. Section-wide inputs (Apstle widget may be outside the form)
   * 3. URL parameter (Apstle sets this in the URL)
   * @returns {string}
   */
  #getSellingPlanId() {
    // 1. Check inside the form
    const form = this.closest('form');
    if (form) {
      const value = this.#findSellingPlanInContainer(form);
      if (value) return value;
    }

    // 2. Check inside the entire section (Apstle widget is outside the form)
    const section = this.closest('.shopify-section, dialog');
    if (section) {
      const value = this.#findSellingPlanInContainer(section);
      if (value) return value;
    }

    // 3. Fall back to URL parameter (Apstle updates the URL)
    const url = new URL(window.location.href);
    return url.searchParams.get('selling_plan') || '';
  }

  /**
   * Searches a container for selling_plan inputs and returns the active value.
   * @param {Element} container
   * @returns {string}
   */
  #findSellingPlanInContainer(container) {
    const inputs = container.querySelectorAll('[name="selling_plan"]');
    for (const input of inputs) {
      const el = /** @type {HTMLInputElement | HTMLSelectElement} */ (input);
      if (el instanceof HTMLInputElement && el.type === 'radio') {
        if (el.checked && el.value) return el.value;
        continue;
      }
      if (el.value) return el.value;
    }
    return '';
  }

  /**
   * Updates the price display inside the buy button.
   * @param {Element} priceContainer
   * @param {number} price - Price in cents
   * @param {number} compareAtPrice - Compare-at price in cents
   */
  #updatePriceDisplay(priceContainer, price, compareAtPrice) {
    const showCompare = compareAtPrice > price;

    let html = '';
    if (showCompare) {
      html += `<span class="compare-at-price">${this.#formatMoney(compareAtPrice)}</span> `;
    }
    html += `<span class="price">${this.#formatMoney(price)}</span>`;

    priceContainer.innerHTML = html;
  }

  /**
   * Formats a price in cents using the store's money format template.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const section = this.closest('.shopify-section, dialog');
    const tpl = section?.querySelector('template[data-money-format]');
    const template = tpl instanceof HTMLTemplateElement
      ? (tpl.content.textContent || '{{amount}}').trim()
      : '{{amount}}';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = 2;

      if (placeholder === 'amount') {
        // defaults
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
      }

      const roundedNumber = (cents / 100).toFixed(precision);
      let [whole, decimal] = roundedNumber.split('.');
      if (!whole) whole = '0';
      if (!decimal) decimal = '';
      whole = whole.replace(/\d(?=(\d{3})+(?!\d))/g, (d) => d + thousandsSeparator);
      return precision <= 0 ? whole : whole + decimalSeparator + decimal.padEnd(precision, '0');
    });
  }

  /**
   * Parses a formatted price string (e.g. "132,09€") to cents.
   * Handles both European (1.234,56) and US (1,234.56) formats.
   * @param {string} priceText
   * @returns {number}
   */
  #parsePriceToCents(priceText) {
    const cleaned = priceText.replace(/[^\d.,]/g, '').trim();
    if (!cleaned) return 0;
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    let normalized;
    if (lastComma > lastDot) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (lastDot > lastComma) {
      normalized = cleaned.replace(/,/g, '');
    } else {
      normalized = cleaned.replace(',', '.');
    }
    return Math.round(parseFloat(normalized) * 100);
  }

  /**
   * Resets selling plan poll state to force re-evaluation on next cycle.
   * Called when the variant changes so the button price updates.
   */
  resetSellingPlanPoll() {
    this.#lastSellingPlanId = null;
    this.#lastVariantId = null;
    this.#lastBundlePriceKey = '';
  }

  /**
   * Reads the current price and compare-at price from a product-price element
   * and updates the buy button's inline price display.
   * @param {Element} productPriceEl
   */
  #syncPriceFromElement(productPriceEl) {
    const priceContainer = this.refs.addToCartButton?.querySelector('.add-to-cart-price');
    if (!priceContainer) return;

    const sourcePrice = productPriceEl.querySelector('.price');
    const sourceCompare = productPriceEl.querySelector('.compare-at-price');

    if (!sourcePrice) return;

    const newPrice = sourcePrice.textContent?.trim() || '';
    const newCompare = sourceCompare?.textContent?.trim() || '';

    let html = '';
    if (newCompare) {
      html += `<span class="compare-at-price">${newCompare}</span>`;
    }
    html += `<span class="price">${newPrice}</span>`;

    if (priceContainer.innerHTML !== html) {
      priceContainer.innerHTML = html;
    }
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Reset selling plan poll so it re-evaluates price for the new variant
    if (addToCartButtonContainer) {
      addToCartButtonContainer.resetSellingPlanPoll();
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
